import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { ROLE_LABELS, ORG_TYPE_LABELS } from './constants';
import './theme.css';
import './CrmLayout.css';

// The CRM shell: a fixed nav rail, a work bar with global search and quick-add, and
// the screen itself. The rail is pinned below HUB's header, whose height is measured
// rather than assumed -- it changes between desktop and mobile.

const SECTIONS = [
  {
    label: 'Work',
    items: [
      { to: '/crm/dashboard', icon: '◈', label: 'Dashboard', badge: 'due' },
      { to: '/crm/pipeline', icon: '▤', label: 'Pipeline', badge: 'slipping', tone: 'warn' },
      { to: '/crm/activity', icon: '◷', label: 'Activity' },
    ],
  },
  {
    label: 'Records',
    items: [
      { to: '/crm/organizations', icon: '⌂', label: 'Accounts', match: '/crm/organizations' },
      { to: '/crm/contacts', icon: '☏', label: 'Contacts' },
    ],
  },
  {
    label: 'Money',
    items: [
      { to: '/crm/invoices', icon: '$', label: 'Invoices', badge: 'unpaid', tone: 'warn' },
      { to: '/crm/reports', icon: '▦', label: 'Reports' },
      { to: '/crm/quotes', icon: '✎', label: 'Quotes', soon: true },
    ],
  },
  {
    label: 'Outreach',
    items: [
      { to: '/crm/email', icon: '✉', label: 'Email', badge: 'unread', tone: 'warn' },
    ],
  },
  {
    label: 'System',
    adminOnly: true,
    items: [
      { to: '/crm/settings', icon: '⚙', label: 'Settings' },
    ],
  },
];

const initials = (first, last) =>
  `${(first || '').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || '?';

const DEFAULT_VIEWER = { id: null, canSeeAll: false, canWrite: false, canAdmin: false };

export const CrmSettingsContext = React.createContext({
  settings: { theme: 'graphite', appearance: 'light', density: 'comfortable', use_team_colors: true },
  settingsLoaded: false,
  viewer: DEFAULT_VIEWER,
  refreshSettings: () => {},
});

const CrmLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settings, setSettings] = useState({
    theme: 'graphite', appearance: 'light', density: 'comfortable',
    default_landing: 'dashboard', use_team_colors: true,
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [viewer, setViewer] = useState(DEFAULT_VIEWER);
  const [counts, setCounts] = useState({});
  const [term, setTerm] = useState('');
  const [results, setResults] = useState(null);
  const searchRef = useRef(null);

  // HUB's header is sticky and its height differs across breakpoints. Measure it so
  // the rail pins flush underneath instead of guessing an offset.
  useEffect(() => {
    const applyOffset = () => {
      const header = document.querySelector('.layout > .header');
      const h = header ? Math.round(header.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--crm-header-h', `${h}px`);
    };
    applyOffset();
    window.addEventListener('resize', applyOffset);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(applyOffset) : null;
    const header = document.querySelector('.layout > .header');
    if (observer && header) observer.observe(header);
    return () => {
      window.removeEventListener('resize', applyOffset);
      if (observer) observer.disconnect();
    };
  }, []);

  const refreshSettings = useCallback(async () => {
    try {
      const response = await api.get('/crm/settings');
      setSettings(response.data.settings);
    } catch {
      /* keep the shipped defaults */
    } finally {
      setSettingsLoaded(true);
    }
  }, []);

  useEffect(() => { refreshSettings(); }, [refreshSettings]);

  // What this person is allowed to see and do -- the server is the authority, this
  // just keeps the UI from offering controls that would be refused.
  useEffect(() => {
    api.get('/crm/meta')
      .then(r => setViewer(r.data.viewer || DEFAULT_VIEWER))
      .catch(() => setViewer(DEFAULT_VIEWER));
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const response = await api.get('/crm/nav-counts');
      setCounts(response.data);
    } catch {
      setCounts({});
    }
  }, []);

  useEffect(() => { fetchCounts(); }, [fetchCounts, location.pathname]);
  useEffect(() => { setDrawerOpen(false); setResults(null); setTerm(''); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setDrawerOpen(false); setResults(null); } };
    const onClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setResults(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, []);

  useEffect(() => {
    if (term.trim().length < 2) { setResults(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const response = await api.get(`/crm/search?q=${encodeURIComponent(term.trim())}`);
        setResults(response.data);
      } catch {
        setResults(null);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [term]);

  const activeItem = SECTIONS
    .flatMap(s => s.items)
    .filter(i => !i.soon)
    .find(i => (i.match && location.pathname.startsWith(i.match)) || location.pathname === i.to);

  const hits = results
    ? (results.organizations?.length || 0) + (results.contacts?.length || 0)
    : 0;

  return (
    <div
      className={`crm-shell${drawerOpen ? ' drawer-open' : ''}`}
      data-crm-theme={settings.theme}
      data-crm-appearance={settings.appearance}
      data-crm-density={settings.density}
    >
      <div className="crm-scrim" onClick={() => setDrawerOpen(false)} aria-hidden="true" />

      <nav className="crm-rail" aria-label="CRM sections">
        <div className="rail-inner">
          <div className="rail-brand">
            <span className="brand-mark">TS</span>
            <span className="brand-text">
              <strong>Team Sales</strong>
              <em>{viewer.canSeeAll ? 'All accounts' : 'My accounts'}</em>
            </span>
          </div>

          <div className="rail-nav">
            {SECTIONS.filter(sec => !sec.adminOnly || viewer.canAdmin).map(section => (
              <div key={section.label} className="rail-section">
                <span className="rail-section-label">{section.label}</span>
                {section.items.map(item => {
                  if (item.soon) {
                    return (
                      <span key={item.to} className="rail-item disabled" title="Not built yet">
                        <span className="rail-icon">{item.icon}</span>
                        <span className="rail-label">{item.label}</span>
                        <span className="soon-tag">Soon</span>
                      </span>
                    );
                  }
                  const count = item.badge ? counts[item.badge] : 0;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `rail-item${isActive || (item.match && location.pathname.startsWith(item.match)) ? ' active' : ''}`
                      }
                    >
                      <span className="rail-icon">{item.icon}</span>
                      <span className="rail-label">{item.label}</span>
                      {count > 0 && <span className={`rail-badge${item.tone ? ` ${item.tone}` : ''}`}>{count}</span>}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </nav>

      <div className="crm-main">
        <header className="crm-workbar">
          <button
            type="button"
            className="drawer-toggle"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open CRM navigation"
          >
            <span /><span /><span />
          </button>

          <h2 className="workbar-title">{activeItem?.label || 'CRM'}</h2>

          <div className="workbar-search" ref={searchRef}>
            <span className="search-icon" aria-hidden="true">⌕</span>
            <input
              type="search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onFocus={() => { if (term.trim().length >= 2 && results) setResults(results); }}
              placeholder="Search accounts and people…"
              aria-label="Search the CRM"
            />
            {results && (
              <div className="search-results">
                {hits === 0 ? (
                  <p className="search-empty">No matches for “{term}”</p>
                ) : (
                  <>
                    {results.organizations.length > 0 && (
                      <div className="search-group">
                        <span className="search-group-label">Accounts</span>
                        {results.organizations.map(o => (
                          <button
                            key={`o-${o.id}`}
                            type="button"
                            className="search-hit"
                            onClick={() => navigate(`/crm/organizations/${o.id}`)}
                          >
                            <span className="hit-avatar org">{(o.name || '?').charAt(0).toUpperCase()}</span>
                            <span className="hit-text">
                              <strong>{o.name}</strong>
                              <em>{[ORG_TYPE_LABELS[o.org_type], o.uil_classification, o.city, o.mascot]
                                .filter(Boolean).join(' · ')}</em>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    {results.contacts.length > 0 && (
                      <div className="search-group">
                        <span className="search-group-label">People</span>
                        {results.contacts.map(c => (
                          <button
                            key={`c-${c.id}`}
                            type="button"
                            className="search-hit"
                            onClick={() => navigate(`/crm/organizations/${c.organization_id}`)}
                          >
                            <span className="hit-avatar">{initials(c.first_name, c.last_name)}</span>
                            <span className="hit-text">
                              <strong>{[c.first_name, c.last_name].filter(Boolean).join(' ')}</strong>
                              <em>{[c.title || ROLE_LABELS[c.role], c.organization_name]
                                .filter(Boolean).join(' · ')}</em>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {viewer.canWrite && (
            <button
              type="button"
              className="workbar-new"
              onClick={() => navigate('/crm/organizations?new=1')}
            >
              <span aria-hidden="true">+</span> <span className="new-text">New account</span>
            </button>
          )}
        </header>

        <div className="crm-screen-wrap">
          <CrmSettingsContext.Provider value={{ settings, settingsLoaded, viewer, refreshSettings }}>
            {children}
          </CrmSettingsContext.Provider>
        </div>
      </div>
    </div>
  );
};

export default CrmLayout;
